import { openDocumentOnClick } from '../helper/documentHelper';
import { withQueryParam } from '../helper/urlHelper';
import React, { useState } from 'react';
import {
  Space,
  Typography,
  Tabs,
  Steps,
  ConfigProvider,
  Divider,
  message,
  Row,
  Col,
  Skeleton,
  Button,
  Flex,
  Spin,
  Empty,
} from 'antd';
import enUS from 'antd/es/locale/en_US';
import WorkReportTable from './WorkReport/WorkReportTable';
import { useTranslation } from 'react-i18next';
import {
  GetInternshipApplicationsById,
  GetInternshipById,
} from '../helper/apiHelper';
import type {
  CombinedInternshipApplicationWithProcesses,
  InternshipProcess,
  ProcessSteps,
} from '../Types/interfaces';
import { StepName } from '../Types/interfaces';
import { Roles } from '../Types/interfaces';
import { EvaluationReportDisplay } from './EvaluationForm/Display/EvaluationReportDisplayComponent';
import styles from '../Assets/Styles/InternshipProcess.module.css';
import FileUploader from './FileUploader';
import WorkSafetyDocumentForm from './WorkSafetyDocumentForm';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../Constants/queryKeys';
import { EMPTY_ARRAY } from '../Constants/constants';
import { useStudent } from '../hooks/useStudent';
import { useCurrentUser } from '../hooks/useCurrentUser';
import noInternshipProcessesImg from '../Assets/Images/NoInternshipProcess.png';
import { useNavigate } from 'react-router-dom';
import { StudyYearDropdown } from './StudyYearDropdown';
import { useCurrentStudyYear } from '../hooks/useStudyYear';
import { FaRegFilePdf } from 'react-icons/fa';

const { Text } = Typography;

const renderStatusForApplication = (
  status: number
): 'wait' | 'process' | 'finish' | 'error' => {
  switch (status) {
    case 0:
      return 'process';
    case 1:
      return 'finish';
    case 2:
      return 'error';
    default:
      return 'wait';
  }
};

const renderStatus = (
  processes: [ProcessSteps] | null,
  stepName: StepName
): 'wait' | 'process' | 'finish' | 'error' => {
  if (processes === null) {
    return 'wait';
  }

  const step = processes.find((e) => e.stepName === stepName);

  if (!step) {
    return 'wait';
  }

  if (step.disabled) {
    return 'wait';
  }

  switch (step.stepStatus) {
    case 0:
      return 'wait';
    case 1:
      return 'process';
    case 2:
      return 'process';
    case 3:
      return 'finish';
    default:
      return 'wait';
  }
};

const isEnabledStep = (
  processes: [ProcessSteps] | null,
  stepName: string
): boolean => {
  if (!processes) return false;
  const step = processes.find((e) => e.stepName === stepName);
  if (!step) return false;
  return step.disabled;
};

const enableSignature = (processes: [ProcessSteps] | null): boolean => {
  if (processes === null) {
    return false;
  }
  const step = processes.find(
    (e) => e.stepName === 'ConventionFillingAndSigningByStudent'
  );

  if (step && !step.disabled) {
    return step.stepStatus === 1 || step.stepStatus === 2;
  }

  return false;
};

const isSigned = (step: ProcessSteps | undefined): boolean => {
  if (step === undefined) {
    return false;
  }

  return step?.stepStatus === 2;
};

const enableStudentWorkReportSignature = (
  processes: [ProcessSteps] | null
): boolean => {
  if (processes === null) {
    return false;
  }
  const step = processes.find(
    (e) => e.stepName === 'SigningWorkReportByStudent'
  );
  return step ? step.stepStatus === 1 || step.stepStatus === 2 : false;
};

// only processes of university organizations have this step. Before the
// application is accepted there is no process yet, so use the organization flag
const hasWorkSafetyStep = (
  item: CombinedInternshipApplicationWithProcesses
): boolean => {
  if (item.processSteps) {
    return item.processSteps.some(
      (e) => e.stepName === StepName.workSafetyInstructionReading
    );
  }
  return item.application.isUniversityOrganization;
};

const enableWorkSafetySignature = (
  processes: [ProcessSteps] | null
): boolean => {
  const step = processes?.find(
    (e) => e.stepName === StepName.workSafetyInstructionReading
  );
  return step?.stepStatus === 1;
};

// the document can only be signed after the student filled in the missing details
const isWorkSafetyDocumentReady = (
  process: InternshipProcess | null | undefined
): boolean => {
  return (
    !!process?.workSafetyDocumentDetails && !!process.workSafetyDocumentUrl
  );
};

const InternshipProcessComponent = (): React.ReactElement => {
  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation();
  const loggedInUser = useCurrentUser();
  const [file, setFile] = useState<File | null>(null);
  const [workSafetyFormProcess, setWorkSafetyFormProcess] =
    useState<InternshipProcess | null>(null);
  // undefined until the student selects a study year, then the dropdown shows the current one
  const [selectedStudyYearId, setSelectedStudyYearId] = useState<number>();
  const { data: currentStudyYear } = useCurrentStudyYear();
  const queryClient = useQueryClient();
  const { data: student, isLoading: studentIsLoading } = useStudent(
    loggedInUser?.id
  );
  const navigate = useNavigate();

  const applicationIdArr = student?.internshipApplications;
  const processIdArr = student?.internshipProcesses;

  const {
    data: internshipApplications = EMPTY_ARRAY,
    isLoading: internshipApplicationsIsLoading,
    refetch: reFetchData,
  } = useQuery({
    queryKey: [
      ...QUERY_KEYS.STUDENT_INTERNSHIP_PROCESSES,
      applicationIdArr,
      processIdArr,
    ],
    queryFn: async () => {
      if (
        applicationIdArr &&
        processIdArr &&
        applicationIdArr.length === 0 &&
        processIdArr.length === 0
      ) {
        return [];
      }

      const internshipProcessesResponse = await Promise.all(
        processIdArr?.filter((e) => e !== 0).map(GetInternshipById) ?? []
      );
      if (!applicationIdArr) return [];
      const internshipApplicationResponse = await Promise.all(
        applicationIdArr
          .filter((e) => e !== 0)
          .map(async (e) => {
            const res = await GetInternshipApplicationsById(e);

            const application = res;
            let process = null;
            let processSteps = null;

            if (application.status === 1) {
              process = internshipProcessesResponse.find(
                (p) => p.internshipApplicationId === application.id
              );

              if (process) {
                processSteps = process.processSteps;
              }
            }
            return {
              application: application,
              process: process,
              processSteps: processSteps,
            };
          })
      );
      return internshipApplicationResponse;
    },
    enabled:
      applicationIdArr &&
      processIdArr &&
      applicationIdArr?.length > 0 &&
      processIdArr?.length > 0,
    meta: {
      onError: (error: unknown) => {
        console.error(error);
        messageApi.error(t('internshipProcessComp.loadError'));
      },
    },
  });

  // the dropdown shows the current study year until the student selects another one, the same is filtered here
  const shownStudyYearId = selectedStudyYearId ?? currentStudyYear?.id;

  const shownProcessItems = internshipApplications.filter(
    (item: CombinedInternshipApplicationWithProcesses) =>
      // hide the pending or rejected internship offers, and the processes of the other study years
      item.process !== null && item.process?.studyYearId === shownStudyYearId
  );

  const SkeletonCards = (): React.ReactElement => {
    return (
      <>
        <Row justify="space-around" style={{ marginTop: '100px' }}>
          <Col span={8}>
            <Skeleton active paragraph={{ rows: 15 }}></Skeleton>
          </Col>
          <Col span={8}>
            <Skeleton active paragraph={{ rows: 15 }}></Skeleton>
          </Col>
        </Row>
        <Row style={{ marginTop: '50px' }}>
          <Skeleton active />
        </Row>
      </>
    );
  };

  const assignedMentorToStudent = (
    item: CombinedInternshipApplicationWithProcesses
  ): boolean => {
    return (
      item.processSteps?.find(
        (e: ProcessSteps) => e.stepName === 'AllocatingTutorFromOrganization'
      )?.stepStatus === 3
    );
  };

  const updateSteps = async (processId: number | undefined): Promise<void> => {
    if (!processId) {
      return;
    }
    await reFetchData();
  };

  const subTitle = t('internshipProcessComp.unsupportedProcess');

  if (studentIsLoading) {
    return (
      <div
        style={{
          width: '100%',
          height: '90%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (
    applicationIdArr &&
    applicationIdArr.length > 0 &&
    processIdArr &&
    processIdArr.length > 0
  ) {
    if (internshipApplicationsIsLoading) {
      return SkeletonCards();
    }
    return (
      <Space direction="vertical" style={{ display: 'block', paddingTop: 10 }}>
        {contextHolder}
        <WorkSafetyDocumentForm
          open={!!workSafetyFormProcess}
          studentId={loggedInUser.id}
          processId={workSafetyFormProcess?.id}
          initialValues={workSafetyFormProcess?.workSafetyDocumentDetails}
          onClose={() => setWorkSafetyFormProcess(null)}
          onSaved={() =>
            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.STUDENT_INTERNSHIP_PROCESSES,
            })
          }
        />
        <Flex
          align="center"
          justify="start"
          gap={8}
          wrap
          style={{ marginBottom: 10 }}
        >
          <Text strong>{t('studyYear.label')}:</Text>
          <StudyYearDropdown
            value={selectedStudyYearId}
            onChange={setSelectedStudyYearId}
          />
        </Flex>
        {shownProcessItems.length === 0 ? (
          <Empty description={t('studyYear.noProcess')} />
        ) : (
          <Tabs
            tabPosition="top"
            centered
            tabBarGutter={16}
            size="large"
            type="card"
            items={shownProcessItems.map(
              (item: CombinedInternshipApplicationWithProcesses) => {
                return {
                  key: String(item.application.internshipOfferId),
                  label: `${String(item.application.internshipOfferName)} | ${String(item.application.organizationName)}`,
                  children: (
                    <>
                      <Typography.Text strong>
                        {t('internshipProcessComp.organization')}:{' '}
                        {item.application.organizationName}
                      </Typography.Text>
                      <br />
                      <Typography.Text strong>
                        {t('internshipProcessComp.status')}:{' '}
                        {((): string => {
                          if (item.application.status === 0) {
                            return ' ' + t('internshipProcessComp.pending');
                          } else if (item.application.status === 1) {
                            return ' ' + t('internshipProcessComp.accepted');
                          } else if (item.application.status === 2) {
                            return ' ' + t('internshipProcessComp.pending');
                          } else {
                            return '';
                          }
                        })()}
                      </Typography.Text>

                      <Row gutter={8} justify={'space-around'}>
                        <Col
                          xs={24}
                          sm={24}
                          lg={12}
                          style={{ maxWidth: 500, marginBottom: 20 }}
                        >
                          <Divider style={{ fontSize: 18 }}>
                            {t('internshipProcessComp.orgProcesses')}
                          </Divider>
                          <Steps
                            direction="vertical"
                            items={[
                              {
                                title: t('internshipProcessComp.accepting'),
                                status: renderStatusForApplication(
                                  item.application.status
                                ),
                              },
                              ...(hasWorkSafetyStep(item)
                                ? [
                                    {
                                      title: t(
                                        'internshipProcessComp.signWorkSafety'
                                      ),
                                      status: renderStatus(
                                        item.processSteps,
                                        StepName.workSafetyInstructionReading
                                      ),
                                    },
                                  ]
                                : []),
                              {
                                title: t('internshipProcessComp.assignMentor'),
                                status: renderStatus(
                                  item.processSteps,
                                  StepName.allocatingTutorFromOrganization
                                ),
                                className: isEnabledStep(
                                  item.processSteps,
                                  StepName.allocatingTutorFromOrganization
                                )
                                  ? styles.blurredStep
                                  : undefined,
                                description: isEnabledStep(
                                  item.processSteps,
                                  StepName.allocatingTutorFromOrganization
                                )
                                  ? subTitle
                                  : undefined,
                              },
                              {
                                title: t(
                                  'internshipProcessComp.fillWorkReport'
                                ),
                                status: renderStatus(
                                  item.processSteps,
                                  StepName.workReportFilling
                                ),
                                className: isEnabledStep(
                                  item.processSteps,
                                  StepName.workReportFilling
                                )
                                  ? styles.blurredStep
                                  : undefined,
                                description: isEnabledStep(
                                  item.processSteps,
                                  StepName.workReportFilling
                                )
                                  ? subTitle
                                  : undefined,
                              },
                              {
                                title: t(
                                  'internshipProcessComp.validateWorkReportByTutor'
                                ),
                                status: renderStatus(
                                  item.processSteps,
                                  StepName.validateWorkReportByTutor
                                ),
                                className: isEnabledStep(
                                  item.processSteps,
                                  StepName.validateWorkReportByTutor
                                )
                                  ? styles.blurredStep
                                  : undefined,
                                description: isEnabledStep(
                                  item.processSteps,
                                  StepName.validateWorkReportByTutor
                                )
                                  ? subTitle
                                  : undefined,
                              },
                              {
                                title: t(
                                  'internshipProcessComp.signWorkReportByStudent'
                                ),
                                status: renderStatus(
                                  item.processSteps,
                                  StepName.signingWorkReportByStudent
                                ),
                                className: isEnabledStep(
                                  item.processSteps,
                                  StepName.signingWorkReportByStudent
                                )
                                  ? styles.blurredStep
                                  : undefined,
                                description: isEnabledStep(
                                  item.processSteps,
                                  StepName.signingWorkReportByStudent
                                )
                                  ? subTitle
                                  : undefined,
                              },
                              {
                                title: t(
                                  'internshipProcessComp.signWorkReportByTutor'
                                ),
                                status: renderStatus(
                                  item.processSteps,
                                  StepName.signingWorkReportByTutor
                                ),
                                className: isEnabledStep(
                                  item.processSteps,
                                  StepName.signingWorkReportByTutor
                                )
                                  ? styles.blurredStep
                                  : undefined,
                                description: isEnabledStep(
                                  item.processSteps,
                                  StepName.signingWorkReportByTutor
                                )
                                  ? subTitle
                                  : undefined,
                              },
                              {
                                title: t(
                                  'internshipProcessComp.completeEvaluationByOrganization'
                                ),
                                status: renderStatus(
                                  item.processSteps,
                                  StepName.completingEvaluationReportByOrganization
                                ),
                                className: isEnabledStep(
                                  item.processSteps,
                                  StepName.completingEvaluationReportByOrganization
                                )
                                  ? styles.blurredStep
                                  : undefined,
                                description: isEnabledStep(
                                  item.processSteps,
                                  StepName.completingEvaluationReportByOrganization
                                )
                                  ? subTitle
                                  : undefined,
                              },
                              {
                                title: t(
                                  'internshipProcessComp.signEvaluationReportByTutor'
                                ),
                                status: renderStatus(
                                  item.processSteps,
                                  StepName.signingEvaluationReportByTutor
                                ),
                                className: isEnabledStep(
                                  item.processSteps,
                                  StepName.signingEvaluationReportByTutor
                                )
                                  ? styles.blurredStep
                                  : undefined,
                                description: isEnabledStep(
                                  item.processSteps,
                                  StepName.signingEvaluationReportByTutor
                                )
                                  ? subTitle
                                  : undefined,
                              },
                            ]}
                          />

                          {enableWorkSafetySignature(item.processSteps) &&
                            !!item.process && (
                              <Row
                                justify="center"
                                align="middle"
                                gutter={[8, 8]}
                                style={{ paddingTop: 10 }}
                              >
                                {/* the details have to be filled in before the document can be signed */}
                                <Col>
                                  <Button
                                    type={
                                      isWorkSafetyDocumentReady(item.process)
                                        ? 'default'
                                        : 'primary'
                                    }
                                    onClick={() =>
                                      setWorkSafetyFormProcess(
                                        item.process ?? null
                                      )
                                    }
                                  >
                                    {isWorkSafetyDocumentReady(item.process)
                                      ? t('workSafetyForm.editButton')
                                      : t('workSafetyForm.fillInButton')}
                                  </Button>
                                </Col>
                                {isWorkSafetyDocumentReady(item.process) && (
                                  <Col>
                                    {/* FileUploader adds its own 8px top margin; cancel it so both buttons share one line */}
                                    <div style={{ marginTop: -8 }}>
                                      <FileUploader
                                        role={Roles.Student}
                                        apiUrl={`students/${loggedInUser.id}/internship-processes/${item.process.id}/work-safety`}
                                        documentUrl={
                                          item.process.workSafetyDocumentUrl
                                        }
                                        documentSigningStatus={
                                          item.process
                                            .currentOrganizationStepName
                                        }
                                        isSignatureDisabled={false}
                                        onFileUpload={async () => {
                                          queryClient.invalidateQueries({
                                            queryKey:
                                              QUERY_KEYS.STUDENT_INTERNSHIP_PROCESSES,
                                          });
                                        }}
                                        alreadySigned={false}
                                      />
                                    </div>
                                  </Col>
                                )}
                              </Row>
                            )}
                        </Col>
                        <Col
                          xs={24}
                          sm={24}
                          lg={12}
                          style={{ maxWidth: 500, marginBottom: 20 }}
                        >
                          {/* Content for the second column */}
                          <Divider style={{ fontSize: 18 }}>
                            {t('internshipProcessComp.universityProcesses')}
                          </Divider>
                          <Steps
                            direction="vertical"
                            items={[
                              {
                                title: t(
                                  'internshipProcessComp.selectSupervisor'
                                ),
                                status: renderStatus(
                                  item.processSteps,
                                  StepName.allocatingUniversitySupervisorFromUBB
                                ),
                                className: isEnabledStep(
                                  item.processSteps,
                                  StepName.allocatingUniversitySupervisorFromUBB
                                )
                                  ? styles.blurredStep
                                  : undefined,
                                description: isEnabledStep(
                                  item.processSteps,
                                  StepName.allocatingUniversitySupervisorFromUBB
                                )
                                  ? subTitle
                                  : undefined,
                              },
                              {
                                title: t(
                                  'internshipProcessComp.registeringConvention'
                                ),
                                status: renderStatus(
                                  item.processSteps,
                                  StepName.registeringConventionDocument
                                ),
                                className: isEnabledStep(
                                  item.processSteps,
                                  StepName.registeringConventionDocument
                                )
                                  ? styles.blurredStep
                                  : undefined,
                                description: isEnabledStep(
                                  item.processSteps,
                                  StepName.registeringConventionDocument
                                )
                                  ? subTitle
                                  : undefined,
                              },
                              {
                                title: t(
                                  'internshipProcessComp.fillConventionByStudent'
                                ),
                                status: renderStatus(
                                  item.processSteps,
                                  StepName.conventionFillingAndSigningByStudent
                                ),
                                className: isEnabledStep(
                                  item.processSteps,
                                  StepName.conventionFillingAndSigningByStudent
                                )
                                  ? styles.blurredStep
                                  : undefined,
                                description: isEnabledStep(
                                  item.processSteps,
                                  StepName.conventionFillingAndSigningByStudent
                                )
                                  ? subTitle
                                  : undefined,
                              },
                              item.application.isUniversityOrganization
                                ? {
                                    title: t(
                                      'internshipProcessComp.signConventionByUniversityProjectCoordinator'
                                    ),
                                    status: renderStatus(
                                      item.processSteps,
                                      StepName.SigningInternshipConventionByUniversityProjectCoordinator
                                    ),
                                    className: isEnabledStep(
                                      item.processSteps,
                                      StepName.SigningInternshipConventionByUniversityProjectCoordinator
                                    )
                                      ? styles.blurredStep
                                      : undefined,
                                    description: isEnabledStep(
                                      item.processSteps,
                                      StepName.SigningInternshipConventionByUniversityProjectCoordinator
                                    )
                                      ? subTitle
                                      : undefined,
                                  }
                                : {
                                    title: t(
                                      'internshipProcessComp.signConventionByOrganization'
                                    ),
                                    status: renderStatus(
                                      item.processSteps,
                                      StepName.signingInternshipConventionByOrganization
                                    ),
                                    className: isEnabledStep(
                                      item.processSteps,
                                      StepName.signingInternshipConventionByOrganization
                                    )
                                      ? styles.blurredStep
                                      : undefined,
                                    description: isEnabledStep(
                                      item.processSteps,
                                      StepName.signingInternshipConventionByOrganization
                                    )
                                      ? subTitle
                                      : undefined,
                                  },
                              {
                                title: t(
                                  'internshipProcessComp.signConventionByTutor'
                                ),
                                status: renderStatus(
                                  item.processSteps,
                                  StepName.signingInternshipConventionByTutor
                                ),
                                className: isEnabledStep(
                                  item.processSteps,
                                  StepName.signingInternshipConventionByTutor
                                )
                                  ? styles.blurredStep
                                  : undefined,
                                description: isEnabledStep(
                                  item.processSteps,
                                  StepName.signingInternshipConventionByTutor
                                )
                                  ? subTitle
                                  : undefined,
                              },
                              {
                                title: t(
                                  'internshipProcessComp.signConventionBySupervisor'
                                ),
                                status: renderStatus(
                                  item.processSteps,
                                  StepName.signingInternshipConventionByUniversitySupervisor
                                ),
                                className: isEnabledStep(
                                  item.processSteps,
                                  StepName.signingInternshipConventionByUniversitySupervisor
                                )
                                  ? styles.blurredStep
                                  : undefined,
                                description: isEnabledStep(
                                  item.processSteps,
                                  StepName.signingInternshipConventionByUniversitySupervisor
                                )
                                  ? subTitle
                                  : undefined,
                              },
                              {
                                title: t(
                                  'internshipProcessComp.signConventionByRector'
                                ),
                                status: renderStatus(
                                  item.processSteps,
                                  StepName.signingInternshipConventionByRector
                                ),
                                className: isEnabledStep(
                                  item.processSteps,
                                  StepName.signingInternshipConventionByRector
                                )
                                  ? styles.blurredStep
                                  : undefined,
                                description: isEnabledStep(
                                  item.processSteps,
                                  StepName.signingInternshipConventionByRector
                                )
                                  ? subTitle
                                  : undefined,
                              },
                              {
                                title: t(
                                  'internshipProcessComp.gradeWorkReportBySupervisor'
                                ),
                                status: renderStatus(
                                  item.processSteps,
                                  StepName.gradingWorkReportByUniversitySupervisor
                                ),
                                className: isEnabledStep(
                                  item.processSteps,
                                  StepName.gradingWorkReportByUniversitySupervisor
                                )
                                  ? styles.blurredStep
                                  : undefined,
                                description: isEnabledStep(
                                  item.processSteps,
                                  StepName.gradingWorkReportByUniversitySupervisor
                                )
                                  ? subTitle
                                  : undefined,
                              },
                            ]}
                          />

                          {/* the convention can be viewed at any time, also after the student signed it;
                            while the student has to sign it, the signing button is next to it */}
                          {item.process &&
                            (!!item.process.conventionDocumentUrl ||
                              enableSignature(item.processSteps)) && (
                              <Row justify="center" style={{ paddingTop: 10 }}>
                                <Space size="middle" align="end" wrap>
                                  {!!item.process.conventionDocumentUrl && (
                                    <Button
                                      icon={<FaRegFilePdf />}
                                      href={withQueryParam(
                                        item.process.conventionDocumentUrl,
                                        'status',
                                        item.process.currentUniversityStepName
                                      )}
                                      onClick={openDocumentOnClick(
                                        item.process.conventionDocumentUrl,
                                        item.process.currentUniversityStepName
                                      )}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {t(
                                        'internshipProcessComp.viewConvention'
                                      )}
                                    </Button>
                                  )}
                                  {enableSignature(item.processSteps) && (
                                    <FileUploader
                                      role={loggedInUser.roles}
                                      apiUrl={`${loggedInUser.id}/upload-internship-process-to-sign`}
                                      documentUrl={
                                        item.process.conventionDocumentUrl
                                      }
                                      documentSigningStatus={
                                        item.process.currentUniversityStepName
                                      }
                                      isSignatureDisabled={false}
                                      onFileUpload={async () => {
                                        queryClient.invalidateQueries({
                                          queryKey:
                                            QUERY_KEYS.STUDENT_INTERNSHIP_PROCESSES,
                                        });
                                      }}
                                      alreadySigned={isSigned(
                                        item.processSteps?.find(
                                          (e) =>
                                            e.stepName ===
                                            'ConventionFillingAndSigningByStudent'
                                        )
                                      )}
                                    />
                                  )}
                                </Space>
                              </Row>
                            )}
                        </Col>
                      </Row>
                      {!!item.process?.workLogId &&
                        assignedMentorToStudent(item) && (
                          <>
                            <Divider plain style={{ fontSize: 20 }}>
                              {t('internshipProcessComp.workReport')}
                            </Divider>

                            <ConfigProvider locale={enUS}>
                              <WorkReportTable
                                StudentId={item.process.studentId}
                                ProcessId={item.process.id}
                                WorkLogId={item.process.workLogId}
                                WorkLogDocumentUrl={
                                  item.process.workLogDocumentUrl
                                }
                                WorkLogDocumentStatus={
                                  item.process.currentOrganizationStepName
                                }
                                editable={
                                  item.process.processSteps.find(
                                    (e: ProcessSteps) =>
                                      e.stepName === 'WorkReportFilling'
                                  )?.stepStatus === 1
                                }
                                signable={enableStudentWorkReportSignature(
                                  item.processSteps
                                )}
                                alreadySigned={isSigned(
                                  item.processSteps?.find(
                                    (e) =>
                                      e.stepName ===
                                      'SigningWorkReportByStudent'
                                  )
                                )}
                                onSubmit={true}
                                onSubmitSuccess={() => {
                                  queryClient.invalidateQueries({
                                    queryKey:
                                      QUERY_KEYS.STUDENT_INTERNSHIP_PROCESSES,
                                  });
                                }}
                                workReport={file}
                                setWorkReport={setFile}
                                role={Roles.Student}
                                onFileUploadSuccess={async () => {
                                  updateSteps(item.process?.id);
                                }}
                              />
                            </ConfigProvider>
                          </>
                        )}
                      <br />
                      {item.process &&
                        item.process.evaluationReportId !== 0 && (
                          <Divider plain style={{ fontSize: 20 }}>
                            {t('internshipProcessComp.evaluationReport')}
                          </Divider>
                        ) && (
                          <EvaluationReportDisplay
                            organizationId={item.process.organizationId}
                            reportId={item.process.evaluationReportId}
                            isSignable={false}
                            studentName={
                              loggedInUser.firstName + loggedInUser.lastName
                            }
                          />
                        )}
                    </>
                  ),
                };
              }
            )}
          />
        )}
      </Space>
    );
  } else {
    return (
      <Row
        justify="center"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          width: '100%',
          flex: 1,
          minHeight: 0,
          padding: '24px',
          overflow: 'hidden',
        }}
      >
        <Col xs={24} sm={16} md={12} lg={18}>
          <img
            src={noInternshipProcessesImg}
            alt="noInternshipProcessesImg"
            style={{
              width: '100%',
              maxWidth: '650px',
              height: 'auto',
            }}
          />

          <Flex
            vertical
            align="center"
            style={{
              maxWidth: '650px',
              textAlign: 'center',
              gap: '16px',
            }}
          >
            <Text strong style={{ fontSize: '24px' }}>
              {t('internshipProcessComp.readyToStartInternshipQuestion')}
            </Text>

            <Text style={{ fontSize: '15px', color: '#595959' }}>
              {t('internshipProcessComp.notAppliedProcessDetailedMessage')}
            </Text>

            <Button
              type="primary"
              onClick={() => navigate('/dashboard/3/student/internship-offers')}
              style={{
                fontSize: '16px',
                height: '48px',
                width: '100%',
                borderRadius: '8px',
                marginTop: '8px',
              }}
            >
              {t('internshipProcessComp.exploreInternshipOffers')}
            </Button>
          </Flex>
        </Col>
      </Row>
    );
  }
};

export default InternshipProcessComponent;
